import React, { useMemo } from "react";
import { formatInTimeZone } from "date-fns-tz";
import { NavLink, useSearchParams } from "react-router-dom";
import "./RacquetGameTeams.css";

function RacquetGameTeams({ game, theme, slug }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const racquetMatches = useMemo(
    () => game?.racquetMatches || [],
    [game?.racquetMatches]
  );

  if (!game) {
    return (
      <div className="rgp-public-page">
        <div className="rgp-empty-state">
          Game information is unavailable.
        </div>
      </div>
    );
  }

  const requestedMatchId = searchParams.get("match");
  const selectedMatch =
    racquetMatches.find(
      (match) =>
        String(match.id) === String(requestedMatchId)
    ) || null;

  const selectedMatchPlayerIds = new Set(
    [
      selectedMatch?.home_player_1_id,
      selectedMatch?.home_player_2_id,
      selectedMatch?.away_player_1_id,
      selectedMatch?.away_player_2_id,
    ]
      .filter(Boolean)
      .map(Number)
  );
  const homeTeamName = game.homeTeam?.name || "Home";
  const awayTeamName = game.awayTeam?.name || "Away";

  const homeTeamId = game.homeTeam?.id || null;
  const awayTeamId = game.awayTeam?.id || null;

  const homeScore = Number(game.score_home ?? 0);
  const awayScore = Number(game.score_away ?? 0);

  const isTeamGame = Boolean(game.homeTeam || game.awayTeam);

  const gameStatus =
    game.status === "completed" || homeScore || awayScore
      ? "FINAL"
      : "SCHEDULED";

  const formattedDateTime = game.date
    ? formatInTimeZone(
      new Date(`${game.date}T${game.time || "00:00"}`),
      "America/Los_Angeles",
      "MMM d, yyyy • h:mm a"
    )
    : "Date TBA";

  const allRosterPlayers = [
    ...(game.homeTeam?.players || []),
    ...(game.awayTeam?.players || []),
  ];

  const getPlayerByRosterId = (rosterId) => {
    if (!rosterId) return null;

    return (
      allRosterPlayers.find(
        (player) => Number(player.roster_id) === Number(rosterId)
      ) || null
    );
  };

  const getMatchPlayerBySide = (match, side, position) => {
    if (!match) return null;

    const associatedPlayer = match?.[`${side}Player${position}`];

    if (associatedPlayer) {
      return associatedPlayer;
    }

    const rosterId = match?.[`${side}_roster_${position}_id`];

    return getPlayerByRosterId(rosterId);
  };

  const getMatchPlayers = (match, side) => {
    return [
      getMatchPlayerBySide(match, side, 1),
      getMatchPlayerBySide(match, side, 2),
    ].filter(Boolean);
  };

  const getSidePlayerIds = (match, side) => {
    return [
      match?.[`${side}_player_1_id`],
      match?.[`${side}_player_2_id`],
    ]
      .filter(Boolean)
      .map(Number);
  };

  const getPlayerName = (player) => {
    if (!player) return "TBD";

    return (
      `${player.firstName || ""} ${player.lastName || ""}`.trim() || "TBD"
    );
  };

  const getPlayerPath = (playerId) => {
    if (!playerId) return "#";

    return slug
      ? `/${slug}/players/${playerId}`
      : `/players/${playerId}`;
  };

  const getTeamPath = (teamId) => {
    if (!teamId) return "#";

    return slug
      ? `/${slug}/teams/${teamId}`
      : `/teams/${teamId}`;
  };

  const renderPlayerNames = (match, side) => {
    const players = getMatchPlayers(match, side);

    if (!players.length) return "TBD";

    return players.map((player, index) => (
      <React.Fragment key={player.id || `${side}-${index}`}>
        {index > 0 && (
          <span className="rgp-player-separator"> / </span>
        )}

        <span className="rgp-match-player-name">
          {getPlayerName(player)}
        </span>
      </React.Fragment>
    ));
  };


  const formatVideoEmbedUrl = (url) => {
    if (!url) return "";

    try {
      const parsedUrl = new URL(url);

      if (
        parsedUrl.hostname.includes("youtube.com") &&
        parsedUrl.searchParams.get("v")
      ) {
        return `https://www.youtube.com/embed/${parsedUrl.searchParams.get("v")}`;
      }

      if (parsedUrl.hostname === "youtu.be") {
        const videoId = parsedUrl.pathname.replace("/", "");

        return `https://www.youtube.com/embed/${videoId}`;
      }

      if (
        parsedUrl.hostname.includes("youtube.com") &&
        parsedUrl.pathname.startsWith("/shorts/")
      ) {
        const videoId = parsedUrl.pathname.split("/shorts/")[1];

        return `https://www.youtube.com/embed/${videoId}`;
      }

      return url;
    } catch (error) {
      console.error("Invalid game video URL:", error);
      return "";
    }
  };
  const formatMatchType = (value) => {
    if (!value) return "Match";

    return String(value)
      .replace(/_/g, " ")
      .split(" ")
      .map(
        (word) =>
          word.charAt(0).toUpperCase() +
          word.slice(1).toLowerCase()
      )
      .join(" ");
  };

  const getMatchLabel = (match) => {
    return (
      match?.match_label ||
      formatMatchType(match?.match_type)
    );
  };

  const formatMatchScore = (scores = []) => {
    if (!scores.length) return "—";

    return [...scores]
      .sort(
        (scoreA, scoreB) =>
          Number(scoreA.set_number || 0) -
          Number(scoreB.set_number || 0)
      )
      .map((score) => {
        const baseScore = `${score.home_score ?? 0}-${score.away_score ?? 0
          }`;

        const hasHomeTiebreak =
          score.home_tiebreak !== null &&
          score.home_tiebreak !== undefined;

        const hasAwayTiebreak =
          score.away_tiebreak !== null &&
          score.away_tiebreak !== undefined;

        if (hasHomeTiebreak || hasAwayTiebreak) {
          return `${baseScore} (${score.home_tiebreak ?? 0}-${score.away_tiebreak ?? 0
            })`;
        }

        return baseScore;
      })
      .join(", ");
  };

  const getWinnerTeam = (match) => {
    if (match?.winner_side === "home") {
      return game.homeTeam || null;
    }

    if (match?.winner_side === "away") {
      return game.awayTeam || null;
    }

    return null;
  };

  const getWinnerText = (match) => {
    const winnerTeam = getWinnerTeam(match);

    if (winnerTeam?.name) {
      return winnerTeam.name;
    }

    if (match?.winner_side === "home") {
      return homeTeamName;
    }

    if (match?.winner_side === "away") {
      return awayTeamName;
    }

    return "Pending";
  };

  const handleSelectMatch = (matchId) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("match", matchId);

    setSearchParams(nextParams, {
      replace: true,
    });
  };

  const statDefinitionsById = {};

  racquetMatches.forEach((match) => {
    (match?.stats || []).forEach((entry) => {
      const statDefinition = entry?.stat;

      if (!entry?.stat_id || !statDefinition) return;
      if (statDefinition.is_visible === false) return;

      statDefinitionsById[entry.stat_id] = {
        id: entry.stat_id,
        name: statDefinition.name || "Stat",
        abbreviation: statDefinition.abbreviation || "",
        displayOrder: statDefinition.display_order ?? 999,
      };
    });
  });

  const visibleStatDefinitions = Object.values(
    statDefinitionsById
  ).sort(
    (statA, statB) =>
      statA.displayOrder - statB.displayOrder
  );
  const normalizeStatName = (value = "") =>
    String(value).trim().toLowerCase();

  const getStatDefinitionByName = (name) =>
    visibleStatDefinitions.find(
      (stat) =>
        normalizeStatName(stat.name) === normalizeStatName(name)
    );

  const forehandWinnersStat =
    getStatDefinitionByName("Forehand Winners");

  const backhandWinnersStat =
    getStatDefinitionByName("Backhand Winners");

  const winnersStat =
    getStatDefinitionByName("Winners");

  const firstServeAttemptsStat =
    getStatDefinitionByName("First Serve Attempts");

  const firstServesInStat =
    getStatDefinitionByName("First Serves In");

  const secondServeAttemptsStat =
    getStatDefinitionByName("Second Serve Attempts");

  const secondServesInStat =
    getStatDefinitionByName("Second Serves In");

  const shotsMadeStat =
    getStatDefinitionByName("Shots Made");

  const shotsMissedStat =
    getStatDefinitionByName("Shots Missed");

  const getStatValue = (row, statDefinition) => {
    if (!statDefinition?.id) return 0;

    return Number(
      row?.valuesByStatId?.[statDefinition.id] || 0
    );
  };

  const calculatePercentage = (made, attempts) => {
    const numericMade = Number(made || 0);
    const numericAttempts = Number(attempts || 0);

    if (numericAttempts <= 0) return "—";

    return `${Math.round(
      (numericMade / numericAttempts) * 100
    )}%`;
  };

  const getFirstServePercentage = (row) =>
    calculatePercentage(
      getStatValue(row, firstServesInStat),
      getStatValue(row, firstServeAttemptsStat)
    );

  const getSecondServePercentage = (row) =>
    calculatePercentage(
      getStatValue(row, secondServesInStat),
      getStatValue(row, secondServeAttemptsStat)
    );

  const getShotConsistencyPercentage = (row) => {
    const shotsMade = getStatValue(row, shotsMadeStat);
    const shotsMissed = getStatValue(row, shotsMissedStat);
    const totalShots = shotsMade + shotsMissed;

    return calculatePercentage(shotsMade, totalShots);
  };
  const createEmptyStatValues = () => {
    const valuesByStatId = {};

    visibleStatDefinitions.forEach((stat) => {
      valuesByStatId[stat.id] = 0;
    });

    return valuesByStatId;
  };

  const homePlayerRowsById = {};
  const awayPlayerRowsById = {};

  const createPlayerRow = (player, teamName, side) => ({
    id: player.id,
    name: getPlayerName(player),
    teamName,
    side,
    matchesPlayed: 0,
    matchWins: 0,
    matchLosses: 0,
    setsWon: 0,
    setsLost: 0,
    gamesWon: 0,
    gamesLost: 0,
    valuesByStatId: createEmptyStatValues(),
  });

  const ensurePlayerRow = (player, side) => {
    if (!player?.id) return null;

    const target =
      side === "home"
        ? homePlayerRowsById
        : awayPlayerRowsById;

    if (!target[player.id]) {
      target[player.id] = createPlayerRow(
        player,
        side === "home"
          ? homeTeamName
          : awayTeamName,
        side
      );
    }

    return target[player.id];
  };

  racquetMatches.forEach((match) => {
    const homePlayers = getMatchPlayers(match, "home");
    const awayPlayers = getMatchPlayers(match, "away");

    const homePlayerIds = getSidePlayerIds(match, "home");
    const awayPlayerIds = getSidePlayerIds(match, "away");

    const matchCompleted =
      match.status === "completed" ||
      Boolean(match.winner_side);

    homePlayers.forEach((player) => {
      const row = ensurePlayerRow(player, "home");

      if (row && matchCompleted) {
        row.matchesPlayed += 1;

        if (match.winner_side === "home") {
          row.matchWins += 1;
        }

        if (match.winner_side === "away") {
          row.matchLosses += 1;
        }
      }
    });

    awayPlayers.forEach((player) => {
      const row = ensurePlayerRow(player, "away");

      if (row && matchCompleted) {
        row.matchesPlayed += 1;

        if (match.winner_side === "away") {
          row.matchWins += 1;
        }

        if (match.winner_side === "home") {
          row.matchLosses += 1;
        }
      }
    });

    (match?.scores || []).forEach((score) => {
      const hasScores =
        score.home_score !== null &&
        score.home_score !== undefined &&
        score.away_score !== null &&
        score.away_score !== undefined;

      if (!hasScores) return;

      const homeGames = Number(score.home_score || 0);
      const awayGames = Number(score.away_score || 0);

      homePlayers.forEach((player) => {
        const row = ensurePlayerRow(player, "home");

        if (!row) return;

        row.gamesWon += homeGames;
        row.gamesLost += awayGames;

        if (score.winner_side === "home") {
          row.setsWon += 1;
        }

        if (score.winner_side === "away") {
          row.setsLost += 1;
        }
      });

      awayPlayers.forEach((player) => {
        const row = ensurePlayerRow(player, "away");

        if (!row) return;

        row.gamesWon += awayGames;
        row.gamesLost += homeGames;

        if (score.winner_side === "away") {
          row.setsWon += 1;
        }

        if (score.winner_side === "home") {
          row.setsLost += 1;
        }
      });
    });

    (match?.stats || []).forEach((entry) => {
      if (!entry?.stat_id) return;
      if (entry?.stat?.is_visible === false) return;

      const playerId = Number(entry.player_id);
      const numericValue = Number(entry.value || 0);

      let player = entry.player || null;
      let side = null;

      if (homePlayerIds.includes(playerId)) {
        side = "home";

        player =
          player ||
          homePlayers.find(
            (item) => Number(item.id) === playerId
          );
      }

      if (awayPlayerIds.includes(playerId)) {
        side = "away";

        player =
          player ||
          awayPlayers.find(
            (item) => Number(item.id) === playerId
          );
      }

      if (!player || !side) return;

      const row = ensurePlayerRow(player, side);

      if (!row) return;

      row.valuesByStatId[entry.stat_id] =
        (row.valuesByStatId[entry.stat_id] || 0) +
        numericValue;
    });
  });

  const sortPlayerRows = (rows) =>
    Object.values(rows).sort((playerA, playerB) =>
      playerA.name.localeCompare(playerB.name)
    );

  const homePlayerRows = sortPlayerRows(
    homePlayerRowsById
  );

  const awayPlayerRows = sortPlayerRows(
    awayPlayerRowsById
  );

  const createTeamTotalRow = (
    playerRows,
    teamName
  ) => {
    const total = {
      id: `${teamName}-total`,
      name: "Team Total",
      teamName,
      matchesPlayed: 0,
      matchWins: 0,
      matchLosses: 0,
      setsWon: 0,
      setsLost: 0,
      gamesWon: 0,
      gamesLost: 0,
      valuesByStatId: createEmptyStatValues(),
    };

    playerRows.forEach((playerRow) => {
      total.matchesPlayed += playerRow.matchesPlayed;
      total.matchWins += playerRow.matchWins;
      total.matchLosses += playerRow.matchLosses;
      total.setsWon += playerRow.setsWon;
      total.setsLost += playerRow.setsLost;
      total.gamesWon += playerRow.gamesWon;
      total.gamesLost += playerRow.gamesLost;

      visibleStatDefinitions.forEach((stat) => {
        total.valuesByStatId[stat.id] +=
          Number(playerRow.valuesByStatId[stat.id] || 0);
      });
    });

    return total;
  };

  const homeTeamTotal = createTeamTotalRow(
    homePlayerRows,
    homeTeamName
  );

  const awayTeamTotal = createTeamTotalRow(
    awayPlayerRows,
    awayTeamName
  );

  const renderTeamPlayerStatsTable = ({
    teamName,
    teamId,
    teamLogo,
    playerRows,
    totalRow,
  }) => (
    <section className="rgp-team-player-card">
      <div className="rgp-team-player-heading">
        <div className="rgp-team-player-heading-main">
          {teamLogo && (
            <img
              src={teamLogo}
              alt={teamName}
              className="rgp-team-player-logo"
            />
          )}

          {teamId ? (
            <NavLink
              to={getTeamPath(teamId)}
              className="rgp-team-player-title"
            >
              {teamName}
            </NavLink>
          ) : (
            <h2 className="rgp-team-player-title">
              {teamName}
            </h2>
          )}
        </div>
      </div>

      <div className="rgp-team-player-table-wrap">
        <table className="rgp-team-player-table">
          <thead>
            <tr>
              <th>Player</th>
              <th>Forehand Winners</th>
              <th>Backhand Winners</th>
              <th>Total Winners</th>
              <th>1st Serve %</th>
              <th>2nd Serve %</th>
              <th>Shot Consistency %</th>


              {visibleStatDefinitions.map((stat) => (
                <th key={stat.id}>
                  {stat.name}
                </th>
              ))}


            </tr>
          </thead>

          <tbody>
            {playerRows.map((playerRow) => {
              const isSelectedMatchPlayer =
                selectedMatchPlayerIds.has(
                  Number(playerRow.id)
                );

              return (
                <tr
                  key={playerRow.id}
                  className={
                    isSelectedMatchPlayer
                      ? "rgp-team-player-row rgp-team-player-row-selected"
                      : "rgp-team-player-row"
                  }
                >
                  <td>
                    <NavLink
                      to={getPlayerPath(playerRow.id)}
                      className="rgp-player-stats-link"
                    >
                      {playerRow.name}
                    </NavLink>
                  </td>


                  <td>{getStatValue(playerRow, forehandWinnersStat)}</td>
                  <td>{getStatValue(playerRow, backhandWinnersStat)}</td>
                  <td>{getStatValue(playerRow, winnersStat)}</td>
                  <td>{getFirstServePercentage(playerRow)}</td>
                  <td>{getSecondServePercentage(playerRow)}</td>
                  <td>{getShotConsistencyPercentage(playerRow)}</td>
                </tr>
              );
            })}

            <tr className="rgp-team-total-row">
              <td>{totalRow.name}</td>


              {visibleStatDefinitions.map((stat) => (
                <td key={stat.id}>
                  {totalRow.valuesByStatId[stat.id] || 0}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );

  return (
    <div className={`rgt-page rgt-theme-${theme || "light"}`}>
      <main className="rgt-shell">

        {/* =========================
          GAME META
      ========================= */}

        <div className="rgt-game-meta">
          <span>{game.location || "Location TBA"}</span>

          <span className="rgt-meta-dot">•</span>

          <span>{formattedDateTime}</span>
        </div>

        {/* =========================
          SCOREBOARD
      ========================= */}

        <section className="rgt-scoreboard-card">

          {/* HOME TEAM */}

          <div className="rgt-scoreboard-team rgt-scoreboard-home">
            {/* {game.homeTeam?.logo ? (
              <img
                src={game.homeTeam.logo}
                alt={homeTeamName}
                className="rgt-team-logo"
              />
            ) : (
              <div className="rgt-team-logo-placeholder">
                {homeTeamName
                  .split(" ")
                  .map((word) => word[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase()}
              </div>
            )} */}

            {homeTeamId ? (
              <NavLink
                to={getTeamPath(homeTeamId)}
                className="rgt-team-name"
              >
                {homeTeamName}
              </NavLink>
            ) : (
              <span className="rgt-team-name">
                {homeTeamName}
              </span>
            )}
          </div>

          {/* HOME SCORE */}

          <div className="rgt-main-score">
            {homeScore}
          </div>

          {/* CENTER */}

          <div className="rgt-scoreboard-center">
            <span
              className={`rgt-status-pill ${gameStatus === "FINAL"
                ? "rgt-status-final"
                : "rgt-status-scheduled"
                }`}
            >
              {gameStatus}
            </span>

            {gameStatus === "FINAL" && (
              <div className="rgt-result-summary">
                {/* {homeScore === awayScore
                  ? "Match tied"
                  : `${homeScore > awayScore
                    ? homeTeamName
                    : awayTeamName
                  } won ${Math.max(
                    homeScore,
                    awayScore
                  )} of ${homeScore + awayScore
                  } matches`} */}
              </div>
            )}
          </div>

          {/* AWAY SCORE */}

          <div className="rgt-main-score">
            {awayScore}
          </div>

          {/* AWAY TEAM */}

          <div className="rgt-scoreboard-team rgt-scoreboard-away">
            {awayTeamId ? (
              <NavLink
                to={getTeamPath(awayTeamId)}
                className="rgt-team-name"
              >
                {awayTeamName}
              </NavLink>
            ) : (
              <span className="rgt-team-name">
                {awayTeamName}
              </span>
            )}

            {/* {game.awayTeam?.logo ? (
              <img
                src={game.awayTeam.logo}
                alt={awayTeamName}
                className="rgt-team-logo"
              />
            ) : (
              <div className="rgt-team-logo-placeholder">
                {awayTeamName
                  .split(" ")
                  .map((word) => word[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase()}
              </div>
            )} */}
          </div>

        </section>
        {game.video_url && (
          <section className="rgp-video-card">
            <div className="rgp-card-heading">
              <h2>Game Video</h2>
            </div>

            <div className="rgp-video-wrapper">
              <iframe
                src={formatVideoEmbedUrl(game.video_url)}
                title="Game Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </section>
        )}
        {/* =========================
          MATCH BREAKDOWN
      ========================= */}

        <section className="rgt-breakdown-card">

          <div className="rgt-section-heading">

            <h2>Match Breakdown</h2>
          </div>

          {racquetMatches.length === 0 ? (
            <div className="rgt-empty-content">
              Match lineup has not been posted yet.
            </div>
          ) : (
            <div className="rgt-table-scroll">

              <table className="rgt-breakdown-table">
                <thead>
                  <tr>
                    <th>Line</th>
                    <th>Type</th>
                    <th>{homeTeamName}</th>
                    <th>{awayTeamName}</th>
                    <th>Score</th>
                    <th>Winner</th>
                  </tr>
                </thead>

                <tbody>
                  {racquetMatches.map((match, index) => {
                    const isSelected =
                      String(requestedMatchId) ===
                      String(match.id);

                    const winnerTeam =
                      getWinnerTeam(match);

                    return (
                      <tr
                        key={match.id}
                        className={
                          isSelected
                            ? "rgt-match-row rgt-match-row-selected"
                            : "rgt-match-row"
                        }
                        onClick={() =>
                          handleSelectMatch(match.id)
                        }
                      >
                        {/* LINE */}

                        <td>
                          <span className="rgt-line-number">
                            {index + 1}
                          </span>
                        </td>

                        {/* MATCH TYPE */}

                        <td>
                          <span className="rgt-match-type">
                            {getMatchLabel(match)}
                          </span>
                        </td>

                        {/* HOME PLAYERS */}

                        <td>
                          <div className="rgt-match-player-list">
                            {getMatchPlayers(match, "home").map(
                              (player, playerIndex) => (
                                <React.Fragment
                                  key={
                                    player.id ||
                                    `home-${playerIndex}`
                                  }
                                >
                                  {playerIndex > 0 && (
                                    <span className="rgt-player-separator">
                                      /
                                    </span>
                                  )}

                                  <NavLink
                                    to={getPlayerPath(player.id)}
                                    className="rgt-player-link"
                                    onClick={(event) =>
                                      event.stopPropagation()
                                    }
                                  >
                                    {getPlayerName(player)}
                                  </NavLink>
                                </React.Fragment>
                              )
                            )}
                          </div>
                        </td>

                        {/* AWAY PLAYERS */}

                        <td>
                          <div className="rgt-match-player-list">
                            {getMatchPlayers(match, "away").map(
                              (player, playerIndex) => (
                                <React.Fragment
                                  key={
                                    player.id ||
                                    `away-${playerIndex}`
                                  }
                                >
                                  {playerIndex > 0 && (
                                    <span className="rgt-player-separator">
                                      /
                                    </span>
                                  )}

                                  <NavLink
                                    to={getPlayerPath(player.id)}
                                    className="rgt-player-link"
                                    onClick={(event) =>
                                      event.stopPropagation()
                                    }
                                  >
                                    {getPlayerName(player)}
                                  </NavLink>
                                </React.Fragment>
                              )
                            )}
                          </div>
                        </td>

                        {/* SCORE */}

                        <td className="rgt-match-score">
                          {formatMatchScore(match.scores)}
                        </td>

                        {/* WINNER */}

                        <td>
                          {winnerTeam?.id ? (
                            <NavLink
                              to={getTeamPath(winnerTeam.id)}
                              className="rgt-winner"
                              onClick={(event) =>
                                event.stopPropagation()
                              }
                            >
                              <span className="rgt-winner-icon">
                                ✓
                              </span>

                              {winnerTeam.name}
                            </NavLink>
                          ) : (
                            <span className="rgt-muted">
                              Pending
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

            </div>
          )}
        </section>

        {/* =========================
          PLAYER STATS
      ========================= */}

        {racquetMatches.length > 0 && (
          <section className="rgt-stats-grid">

            {/* HOME TEAM STATS */}

            <section className="rgt-stats-card">

              <div className="rgt-section-heading">

                {homeTeamId ? (
                  <NavLink
                    to={getTeamPath(homeTeamId)}
                    className="rgt-section-title-link"
                  >
                    {homeTeamName} Player Stats
                  </NavLink>
                ) : (
                  <h2>
                    {homeTeamName} Player Stats
                  </h2>
                )}
              </div>

              <div className="rgt-table-scroll">
                <table className="rgt-stats-table">

                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Forehand Winners</th>
                      <th>Backhand Winners</th>
                      <th>Total Winners</th>
                      <th>1st Serve %</th>
                      <th>2nd Serve %</th>
                      <th>Shot Consistency %</th>
                    </tr>
                  </thead>

                  <tbody>
                    {homePlayerRows.map((playerRow) => {
                      const isSelected =
                        selectedMatchPlayerIds.has(
                          Number(playerRow.id)
                        );

                      return (
                        <tr
                          key={playerRow.id}
                          className={
                            isSelected
                              ? "rgt-stats-row rgt-stats-row-selected"
                              : "rgt-stats-row"
                          }
                        >
                          <td>
                            <NavLink
                              to={getPlayerPath(playerRow.id)}
                              className="rgt-player-link"
                            >
                              {playerRow.name}
                            </NavLink>
                          </td>

                          <td>{getStatValue(playerRow, forehandWinnersStat)}</td>
                          <td>{getStatValue(playerRow, backhandWinnersStat)}</td>
                          <td>{getStatValue(playerRow, winnersStat)}</td>
                          <td>{getFirstServePercentage(playerRow)}</td>
                          <td>{getSecondServePercentage(playerRow)}</td>
                          <td>{getShotConsistencyPercentage(playerRow)}</td>
                        </tr>
                      );
                    })}
                  </tbody>

                </table>
              </div>

            </section>

            {/* AWAY TEAM STATS */}

            <section className="rgt-stats-card">

              <div className="rgt-section-heading">

                {awayTeamId ? (
                  <NavLink
                    to={getTeamPath(awayTeamId)}
                    className="rgt-section-title-link"
                  >
                    {awayTeamName} Player Stats
                  </NavLink>
                ) : (
                  <h2>
                    {awayTeamName} Player Stats
                  </h2>
                )}
              </div>

              <div className="rgt-table-scroll">
                <table className="rgt-stats-table">

                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Forehand Winners</th>
                      <th>Backhand Winners</th>
                      <th>Total Winners</th>
                      <th>1st Serve %</th>
                      <th>2nd Serve %</th>
                      <th>Shot Consistency %</th>
                    </tr>
                  </thead>

                  <tbody>
                    {awayPlayerRows.map((playerRow) => {
                      const isSelected =
                        selectedMatchPlayerIds.has(
                          Number(playerRow.id)
                        );

                      return (
                        <tr
                          key={playerRow.id}
                          className={
                            isSelected
                              ? "rgt-stats-row rgt-stats-row-selected"
                              : "rgt-stats-row"
                          }
                        >
                          <td>
                            <NavLink
                              to={getPlayerPath(playerRow.id)}
                              className="rgt-player-link"
                            >
                              {playerRow.name}
                            </NavLink>
                          </td>

                          <td>{getStatValue(playerRow, forehandWinnersStat)}</td>
                          <td>{getStatValue(playerRow, backhandWinnersStat)}</td>
                          <td>{getStatValue(playerRow, winnersStat)}</td>
                          <td>{getFirstServePercentage(playerRow)}</td>
                          <td>{getSecondServePercentage(playerRow)}</td>
                          <td>{getShotConsistencyPercentage(playerRow)}</td>
                        </tr>
                      );
                    })}
                  </tbody>

                </table>
              </div>

            </section>

          </section>
        )}

      </main>
    </div>
  );
}

export default RacquetGameTeams;